import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import React, { useState } from "react";
import { Button } from "../../components/UI/Button";
import CountTextInput from "../../components/UI/CountTextInput";
import TextInput from "../../components/UI/TextInput";
import AddressInput from "../../components/UI/AddressInput";
import Selection from "../../components/UI/Selection";
import { FAQ_CATEGORY_LABELS } from "../../../domain/types/content";
import CheckBox from "../../components/UI/CheckBox";
import Table from "../../components/UI/Table";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";
import { LucidePlus } from "lucide-react";

const ShippingFee = () => {
  const [selectedOption, setSelectedOption] = useState("_");

  interface ShippingFeeSettings {
    feeType: string;
    feeChargeType: "OrderAmount" | "TotalAmount";
    pointCharge: boolean;
    priceRange: { id: number; min: number; max: number; fee: number }[];
  }

  const loadFromLocalStorage = () => {
    let shippingFeeSettings = localStorage.getItem("shippingFeeSettings");
    if (shippingFeeSettings) {
      return JSON.parse(shippingFeeSettings) as ShippingFeeSettings;
    } else {
      saveToLocalStorage({
        feeType: "_",
        feeChargeType: "OrderAmount",
        pointCharge: false,
        priceRange: [{ id: 1, min: 0, max: 50000, fee: 3500 }],
      });

      shippingFeeSettings = localStorage.getItem("taxReceiptOrders");

      if (!shippingFeeSettings) throw new Error("shippingFeeSettings is null");
      return JSON.parse(shippingFeeSettings) as ShippingFeeSettings;
    }
  };

  const saveToLocalStorage = (fontList: ShippingFeeSettings) => {
    localStorage.setItem("shippingFeeSettings", JSON.stringify(fontList));
    return true;
  };

  const [loadedData, setLoadedData] = useState<ShippingFeeSettings>(
    loadFromLocalStorage(),
  );

  const loadData = () => {
    setLoadedData(loadFromLocalStorage());
  };

  const {
    notifications,
    addItem: addNotificationItem,
    removeItem,
  } = useNotifications();

  return (
    <>
      <>
        <NotificationsList notifications={notifications} />
      </>
      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] / flex flex-row justify-between gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">배송비 관리</h2>
          </div>

          <div>
            <div
              className="pl-0 md:pl-5 / grid grid-cols-1 gap-[1.25rem] md:gap-4
          / text-[14px] text-left / w-full max-w-[45rem]"
            >
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-start">
                <div className="mt-2">배송비 유형</div>
                <div className="h-[45px]">
                  <Selection
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    options={[{ value: "_", name: "금액별 배송" }]}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-start">
                <div className="mt-2">금액별 배송</div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-[8px]">
                    <div
                      onClick={() => {
                        const newSettings = {
                          ...loadedData,
                          feeChargeType: "OrderAmount" as const,
                        };
                        setLoadedData(newSettings);
                      }}
                    >
                      <CheckBox
                        isEnabled={loadedData.feeChargeType === "OrderAmount"}
                      />
                    </div>

                    <div>주문금액(할인 전 판매가 기준)</div>
                  </div>
                  <div className="flex flex-col gap-1 mb-2">
                    <div className="flex items-center gap-[8px]">
                      <div
                        onClick={() => {
                          const newSettings = {
                            ...loadedData,
                            feeChargeType: "TotalAmount" as const,
                          };
                          setLoadedData(newSettings);
                        }}
                      >
                        <CheckBox
                          isEnabled={loadedData.feeChargeType === "TotalAmount"}
                        />
                      </div>

                      <div>결제금액(최종 결제금액 기준)</div>
                    </div>
                    <div className="ml-[25px] flex flex-col gap-1">
                      <div
                        className={`flex items-center gap-[8px] ${loadedData.feeChargeType === "OrderAmount" ? "opacity-[0.4]" : null}`}
                      >
                        <span className="text-gray-400">----</span>
                        <div
                          onClick={() => {
                            if (loadedData.feeChargeType === "OrderAmount")
                              return;
                            const newSettings = {
                              ...loadedData,
                              pointCharge: !loadedData.pointCharge,
                            };
                            setLoadedData(newSettings);
                          }}
                        >
                          <CheckBox isEnabled={loadedData.pointCharge} />
                        </div>

                        <div>사용 적립금</div>
                      </div>
                      <div className="w-full max-w-[400px] text-gray-400">
                        예) 50,000원 미만일 경우 배송비 2,500원 부과시
                        50,000원의 상품을 구매하고 적립금을 1,000원 사용한 경우.
                      </div>
                      <div className="w-full max-w-[400px] text-gray-400">
                        <ul>
                          <li>- 사용 적립금 체크: 배송비 0원</li>
                          <li>- 사용 적립금 미체크: 배송비 2500원</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <Table
                    columnWidths={["300px", "300px", "100px"]}
                    tabledata={{
                      head: [<>금액 범위</>, <>배송비</>, <></>],
                      body: loadedData.priceRange.map((priceRangeItem) => [
                        <div className="flex flex-col gap-[8px]">
                          <div className="flex gap-[8px] items-center">
                            <input
                              className="p-1 outline rounded-md outline-gray-200"
                              type="text"
                              placeholder="0"
                              value={priceRangeItem.min}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                setLoadedData((data) => ({
                                  ...data,
                                  priceRange: data.priceRange.map(item =>
                                    item.id === priceRangeItem.id
                                      ? { ...item, min: newValue }
                                      : item
                                  )
                                }));
                              }}
                            />
                            원 이상 ~
                          </div>
                          <div className="flex gap-[8px] items-center">
                            <input
                              className="p-1 outline rounded-md outline-gray-200"
                              type="text"
                              placeholder="50000"
                              value={priceRangeItem.max}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                setLoadedData((data) => ({
                                  ...data,
                                  priceRange: data.priceRange.map(item =>
                                    item.id === priceRangeItem.id
                                      ? { ...item, max: newValue }
                                      : item
                                  )
                                }));
                              }}
                            />
                            원 미만
                          </div>
                        </div>,
                        <div className="flex gap-[8px] items-center">
                          <input
                            className="p-1 outline rounded-md outline-gray-200"
                            type="text"
                            placeholder="3500"
                            value={priceRangeItem.fee}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              setLoadedData((data) => ({
                                ...data,
                                priceRange: data.priceRange.map(item =>
                                  item.id === priceRangeItem.id
                                    ? { ...item, fee: newValue }
                                    : item
                                )
                              }));
                            }}
                          />
                          원
                        </div>,
                        <>
                          <Button
                            onClick={() => {
                              const confirm =
                                window.confirm("정말로 삭제하시겠습니까?");

                              if (!confirm) return;

                              setLoadedData((data) => ({
                                ...data,
                                priceRange: data.priceRange.filter(
                                  (item) => priceRangeItem.id !== item.id,
                                ),
                              }));
                            }}
                            variant="danger"
                          >
                            <LucidePlus className="rotate-45" />
                          </Button>
                        </>,
                      ]),
                    }}
                  ></Table>
                  <div className="w-full flex justify-end">
                    <Button
                      onClick={() => {
                        setLoadedData((data) => ({
                          ...data,
                          priceRange: [
                            ...data.priceRange,
                            {
                              id: data.priceRange[data.priceRange.length - 1].id + 1,
                              min: 0,
                              max: 1000,
                              fee: 3000,
                            },
                          ],
                        }));
                      }}
                    >
                      추가하기
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8"></div>
          <div className="flex justify-center gap-[0.75rem]">
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                window.location.reload();
              }}
              className="w-[8.75rem]"
            >
              취소
            </Button>
            <Button
              className="w-[8.75rem]"
              type="submit"
              variant="primary"
              size="lg"
              onClick={() => {
                saveToLocalStorage(loadedData);
                loadData();
                addNotificationItem("정보가 저장되었습니다.");
              }}
            >
              저장
            </Button>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default ShippingFee;
